<template>
  <div class="c-individual-workflow-status">
    <div class="flex items-center justify-between">
      <span class="left">{{ statusName ?? 'Status' }}</span>
      <BittsTooltip data-testid="workflow-status-tooltip">
        <BittsBadge
          :text="statusText"
          :status="status === 'pending' ? 'warning' : status"
        />
        <template #title v-if="calloutText || lastPushedInTooltip">
          <div class="flex flex-col gap-8">
            <span v-if="calloutText" v-html="calloutText" />
            <span v-if="lastPushedInTooltip">
              Last pushed: {{ formattedLastPushed }}
            </span>
          </div>
        </template>
      </BittsTooltip>
    </div>
    <div
      class="flex items-center justify-between"
      v-if="lastPushed && !lastPushedInTooltip"
    >
      <span class="left">Last pushed</span>
      <span class="text-neutral-text-strong">{{ formattedLastPushed }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsBadge, BittsTooltip, type IStatus } from '@crossbeam/bitts';
import { StrictExtract } from '@crossbeam/types';

import { DateTime } from 'luxon';
import { computed } from 'vue';

const {
  status,
  lastPushed = null,
  lastPushedInTooltip = false,
  calloutText,
  statusName,
} = defineProps<{
  status: StrictExtract<IStatus, 'success' | 'warning' | 'error'> | 'pending';
  lastPushed: string | null; // ISO date string
  statusName?: string;
  calloutText?: string | null;
  lastPushedInTooltip?: boolean;
}>();

const formatDateTime = (isoString: string | null) => {
  return isoString
    ? DateTime.fromISO(isoString).toFormat("LLL dd, yyyy 'at' h:mm a")
    : '--';
};

const formattedLastPushed = computed(() => formatDateTime(lastPushed));

const statusText = computed(() => {
  switch (status) {
    case 'success':
      return 'Active';
    case 'warning':
      return 'Paused';
    case 'error':
      return 'Action needed';
    case 'pending':
      return 'Pending';
    default:
      return 'Unknown';
  }
});
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-individual-workflow-status {
  @apply flex flex-col gap-8;
}
.left {
  @apply text-neutral-text-weak;
}
</style>
